import { useRef, useState } from 'react';
import BottomDrawer from './BottomDrawer';
import MinusIcon from './icons/MinusIcon';
import PlusIcon2 from './icons/PlusIcon2';

interface SpeedDrawerProps {
  playbackRate: number;
  setPlaybackRate: (rate: number) => void;
  variant?: 'absolute' | 'inline';
  min?: number;
  max?: number;
  step?: number;
}

const PRESET_SPEEDS = [1.0, 1.2, 1.4, 1.6, 1.8, 2.0];
const SLIDER_WIDTH = 200;
const KNOB_SIZE = 24;

const SpeedDrawer = ({
  playbackRate,
  setPlaybackRate,
  variant = 'absolute',
  min = 0.5,
  max = 2.5,
  step = 0.1,
}: SpeedDrawerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const isAbsolute = variant === 'absolute';

  // Calculate position from value
  const valueToPosition = (value: number) => {
    const percent = (value - min) / (max - min);
    return percent * (SLIDER_WIDTH - KNOB_SIZE);
  };

  // Calculate value from position
  const positionToValue = (x: number) => {
    const percent = x / (SLIDER_WIDTH - KNOB_SIZE);
    const rawValue = min + percent * (max - min);
    // Snap to step
    const snapped = Math.round(rawValue / step) * step;
    return Math.max(min, Math.min(max, snapped));
  };

  const handleInteraction = (clientX: number) => {
    if (!trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const x = Math.max(
      0,
      Math.min(SLIDER_WIDTH - KNOB_SIZE, clientX - rect.left - KNOB_SIZE / 2)
    );
    setPlaybackRate(positionToValue(x));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    isDragging.current = true;
    handleInteraction(e.clientX);

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging.current) handleInteraction(e.clientX);
    };
    const handleMouseUp = () => {
      isDragging.current = false;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    isDragging.current = true;
    handleInteraction(e.touches[0].clientX);

    const handleTouchMove = (e: TouchEvent) => {
      if (isDragging.current) handleInteraction(e.touches[0].clientX);
    };
    const handleTouchEnd = () => {
      isDragging.current = false;
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };

    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);
  };

  const adjustSpeed = (delta: number) => {
    const newValue = Math.round((playbackRate + delta) / step) * step;
    setPlaybackRate(Math.max(min, Math.min(max, newValue)));
  };

  const knobPosition = valueToPosition(playbackRate);
  const fillWidth = knobPosition + KNOB_SIZE / 2;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '8px',
        ...(isAbsolute && {
          position: 'absolute',
          right: '10px',
          top: '50%',
          transform: 'translateY(-50%)',
        }),
      }}
    >
      <button
        onClick={() => setIsOpen(true)}
        style={{
          border: 'none',
          background: 'transparent',
          color: 'var(--gold)',
          fontSize: isAbsolute ? '1.3rem' : '1.2rem',
          fontWeight: isAbsolute ? 400 : 600,
          cursor: 'pointer',
          padding: isAbsolute ? 0 : '0.5rem',
          lineHeight: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
        title="Playback speed"
      >
        <span>{playbackRate.toFixed(1)}x</span>
        {!isAbsolute && (
          <span
            style={{
              fontSize: '0.66rem',
              fontWeight: 400,
              marginTop: '0px',
            }}
          >
            speed
          </span>
        )}
      </button>

      <BottomDrawer isOpen={isOpen} onClose={() => setIsOpen(false)}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 24,
          }}
        >
          <span
            style={{
              fontSize: '1.1rem',
              fontWeight: 500,
              color: 'var(--primary)',
            }}
          >
            Speed
          </span>
          <span
            style={{
              fontSize: '1.1rem',
              fontWeight: 600,
              color: 'var(--accent)',
            }}
          >
            {playbackRate.toFixed(2)}x
          </span>
        </div>

        {/* Slider with +/- buttons */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            marginBottom: 24,
          }}
        >
          {/* Minus button */}
          <button
            onClick={() => adjustSpeed(-step)}
            disabled={playbackRate <= min}
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: '1px solid var(--border-strong)',
              backgroundColor: 'var(--card-bg)',
              cursor: playbackRate <= min ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              padding: 0,
            }}
          >
            <MinusIcon
              size={20}
              color={
                playbackRate <= min
                  ? 'var(--primary-disabled)'
                  : 'var(--primary)'
              }
            />
          </button>

          {/* Slider track */}
          <div
            ref={trackRef}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            style={{
              position: 'relative',
              width: SLIDER_WIDTH,
              height: 8,
              backgroundColor: 'var(--border)',
              borderRadius: 4,
              cursor: 'pointer',
              touchAction: 'none',
            }}
          >
            {/* Fill */}
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                height: '100%',
                width: fillWidth,
                backgroundColor: 'var(--accent-soft)',
                borderRadius: 4,
              }}
            />
            {/* Knob */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: knobPosition,
                transform: 'translateY(-50%)',
                width: KNOB_SIZE,
                height: KNOB_SIZE,
                backgroundColor: 'var(--accent)',
                borderRadius: '50%',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              }}
            />
          </div>

          {/* Plus button */}
          <button
            onClick={() => adjustSpeed(step)}
            disabled={playbackRate >= max}
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: '1px solid var(--border-strong)',
              backgroundColor: 'var(--card-bg)',
              cursor: playbackRate >= max ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              padding: 0,
            }}
          >
            <PlusIcon2
              size={20}
              color={
                playbackRate >= max
                  ? 'var(--primary-disabled)'
                  : 'var(--primary)'
              }
            />
          </button>
        </div>

        {/* Preset speed buttons */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 8,
          }}
        >
          {PRESET_SPEEDS.map((speed) => {
            const isSelected = Math.abs(playbackRate - speed) < 0.05;
            return (
              <button
                key={speed}
                onClick={() => setPlaybackRate(speed)}
                style={{
                  padding: '10px 14px',
                  borderRadius: 8,
                  border: isSelected
                    ? '2px solid var(--accent)'
                    : '1px solid var(--border-strong)',
                  backgroundColor: isSelected
                    ? 'var(--accent-soft)'
                    : 'var(--card-bg)',
                  color: isSelected ? 'var(--accent)' : 'var(--primary)',
                  fontSize: '0.95rem',
                  fontWeight: isSelected ? 600 : 400,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
              >
                {speed === 1.0 ? '1' : speed.toFixed(1)}
              </button>
            );
          })}
        </div>
      </BottomDrawer>
    </div>
  );
};

export default SpeedDrawer;
